import isEmpty from 'lodash/isEmpty';
import { createSelector } from 'reselect';

import i18n from 'src/config/i18n';
import { formatProgramWeekUnlockDate } from 'src/shared/helpers/formatProgramWeekUnlockDate';
import { getNumberOrdinal } from 'src/shared/helpers/getNumberOrdinal';

import { ProgramWeekEnum } from '../models/programs';
import {
    extractProgramId,
    getPreviousWeekCompletionDate,
    getProgramWeeksDataByProgramId,
    getSingleProgramSingleWeek,
} from './programsSelectors';

export const getProgramCardRedirectUrl = createSelector(
    getProgramWeeksDataByProgramId,
    extractProgramId,
    (weeks, programId): string => {
        if (!weeks || isEmpty(weeks)) {
            return '/dashboard';
        }

        return weeks.length > 1
            ? `/dashboard/programs/${programId}`
            : `/programs/${programId}/${ProgramWeekEnum.WeekOne}`;
    },
);

export const getProgramCardTooltip = createSelector(getProgramWeeksDataByProgramId, (weeks):
    | string
    | undefined => {
    if (!weeks || isEmpty(weeks)) {
        return;
    }

    return i18n.t(`dashboard.programs.${weeks.length > 1 ? 'showProgramWeeks' : 'startProgram'}`);
});

export const getProgramCardWeeksLength = createSelector(getProgramWeeksDataByProgramId, (weeks):
    | string
    | undefined => {
    if (!weeks || isEmpty(weeks)) {
        return;
    }

    return `${weeks.length} ${i18n.t(`common.${weeks.length > 1 ? 'weeks' : 'week'}`)}`;
});

export const getProgramWeekCardIndex = createSelector(
    getSingleProgramSingleWeek,
    (week): string | undefined =>
        week && `${getNumberOrdinal(week.readableWeekNumber)} ${i18n.t('common.week')}`,
);

export const getProgramWeekCardUnlockText = createSelector(
    getPreviousWeekCompletionDate,
    getSingleProgramSingleWeek,
    (previousWeekCompletionDate, week): string | undefined => {
        if (!week) {
            return undefined;
        }

        const conditionalWeekUnlockText = i18n.t('dashboard.programs.conditionalWeekUnlock', {
            number: week.weekNumber,
        });
        const timedWeekUnlockText = i18n.t('dashboard.programs.timedWeekUnlock');

        return previousWeekCompletionDate
            ? formatProgramWeekUnlockDate(previousWeekCompletionDate, timedWeekUnlockText)
            : conditionalWeekUnlockText;
    },
);
